import { useCallback } from 'react';

import { DEFAULT_CURRENCY } from '@/data/config/DEFAULTS';
import { usePersonInfo } from '@/data/Content/PersonInfo';
import { useSettings } from '@/data/Settings';
import { dFix } from '@/data/utils/floatingPoint';
import { FacebookClientPayload } from '@/pages/api/tracking/facebook/facebookAPIUtils';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackingStoreLocation } from 'presentation/events/hooks/useTrackingStoreLocation';
import { useFacebookClickIdentifiers } from 'presentation/events/hooks/useClickIdentifiers';
import { triggerFacebookCAPIEvent } from 'presentation/events/services/facebookService';
import { safeDecodeUrl } from 'presentation/events/utils/ga4Utils';
import {
	getItemPartNumber,
	getProductBrand,
	getProductPrice,
	getProductQuantity,
} from 'presentation/events/utils/productUtils';
import { SendAddToCartEventProps } from './types';
import { getExternalId } from '@/data/utils/externalId';

export const useTrackAddToCartFacebook = () => {
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const storeLocation = useTrackingStoreLocation();
	const { personInfo } = usePersonInfo();
	const { fbc, fbp } = useFacebookClickIdentifiers();

	return useCallback(
		(props: SendAddToCartEventProps) => {
			if (settings.userData?.ENABLE_CAPI_TARGET_CALL !== 'true' || !physicalStoreId) return;

			const { eventId, storeId } = props;

			let contents: Array<{
				id: string;
				quantity: number;
				item_price: number;
				category: string;
				brand: string;
			}> = [];

			let contentCategory: string | undefined;

			if (props.product) {
				// Single product

				// TODO: bundle logic needs to be implemented

				const { product, promotionDetails, categories } = props;

				const quantity = getProductQuantity({ quantity: props.quantity });

				const { price } = getProductPrice({
					product,
					quantity,
					physicalStoreId,
					promotionDetails,
				});

				contentCategory = categories.join(' > ');
				contents = [
					{
						id: getItemPartNumber(product),
						quantity,
						item_price: price,
						category: contentCategory,
						brand: getProductBrand(product),
					},
				];
			}

			if (props.items) {
				// Multiple products

				contents = props.items.map((item) => {
					const { product, promotionDetails } = item;
					const quantity = getProductQuantity({ quantity: item.quantity });
					const { price } = getProductPrice({
						product,
						quantity,
						physicalStoreId,
						promotionDetails,
					});

					return {
						id: getItemPartNumber(product),
						quantity,
						item_price: price,
						category: item.categories.join(' > '),
						brand: getProductBrand(product),
					};
				});
			}

			const value = dFix(
				contents.reduce((sum, item) => sum + item.item_price * item.quantity, 0),
				2
			);
			const content_ids = contents.map((c) => c.id);

			const payload: FacebookClientPayload = {
				event_name: 'AddToCart',
				event_id: eventId,
				storeId,
				event_source_url: safeDecodeUrl(window.location.href),
				test_event_code: settings?.userData?.FACEBOOK_TEST_CODE,
				fbc,
				fbp,
				custom_data: {
					content_ids,
					contents,
					content_type: 'product',
					content_category: contentCategory,
					currency: DEFAULT_CURRENCY,
					value,
					externalId: getExternalId(),
					email: personInfo?.email1,
					phone: personInfo?.phone1,
					firstName: personInfo?.firstName,
					lastName: personInfo?.lastName,
					city: storeLocation?.city,
					state: storeLocation?.state,
					zipCode: storeLocation?.zipCode,
				},
			};

			triggerFacebookCAPIEvent(payload);
		},
		[
			settings.userData?.ENABLE_CAPI_TARGET_CALL,
			settings.userData?.FACEBOOK_TEST_CODE,
			physicalStoreId,
			storeLocation,
			fbc,
			fbp,
			personInfo?.email1,
			personInfo?.phone1,
			personInfo?.firstName,
			personInfo?.lastName,
		]
	);
};
